/**
 * AudioListenScreen — the Listen tab catalog (AUDIO-LESSONS-001).
 *
 * Mirrors RolePlayCollectionsScreen: an outer ScrollView with inner grids
 * (`scrollEnabled={false}`). Series render as cards grouped by kind/level — a
 * "Grammar on the go" group of `lesson_linked` series bucketed by level, plus
 * `standalone` series shown as their own cards. Tapping a series opens
 * AudioSeriesScreen. The catalog renders for free users too (preview); playback
 * is gated at the player (AUDIO-LESSONS-007).
 */

import React, { useCallback, useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useResolveBilingual, slugToTitle } from '../hooks/useResolveBilingual';
import { errorMessage } from '../generated/api/endpoint-errors';
import { useAudioLessonsStore, type AudioSeriesSummary } from '../store/audioLessonsStore';
import {
  selectAudioSeries,
  selectIsLoadingAudioSeries,
  selectAudioSeriesError,
  selectFetchAudioSeriesList,
  selectClearAudioSeriesError,
  selectHydrateAudioProgress,
  selectFetchAudioProgress,
} from '../store/audioLessonsSelectors';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectActiveCourseId } from '../store/userPreferencesSelectors';

interface SeriesCardProps {
  series: AudioSeriesSummary;
  onPress: (slug: string) => void;
}

const SeriesCard = React.memo(function SeriesCard({ series, onPress }: SeriesCardProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const resolve = useResolveBilingual();
  const handlePress = useCallback(() => onPress(series.slug), [onPress, series.slug]);

  const title = resolve(series.title) || slugToTitle(series.slug);
  const count = series.episodeCount;
  const completed = series.completedCount;
  const hasProgress = count > 0 && completed > 0;

  return (
    <TouchableOpacity
      testID={`audio-lessons-series-card-${series.slug}`}
      accessibilityRole="button"
      accessibilityLabel={t('audioLessons.seriesCardLabel', { title, count })}
      onPress={handlePress}
      activeOpacity={0.75}
      style={styles.cardWrap}
    >
      <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
        {series.level != null && (
          <View style={[styles.levelPill, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: 11 * fontScale, fontWeight: '700', color: theme.textSecondary, ...ffs }}
            >
              {series.level}
            </Text>
          </View>
        )}
        <Text
          allowFontScaling={false}
          numberOfLines={2}
          style={{ fontSize: 16 * fontScale, fontWeight: '700', color: theme.text, marginTop: 8, ...ffs }}
        >
          {title}
        </Text>
        <Text
          allowFontScaling={false}
          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 6, ...ffs }}
        >
          {hasProgress
            ? t('audioLessons.seriesProgress', { completed, total: count })
            : t('audioLessons.episodeCount', { count })}
        </Text>
        {hasProgress && (
          <View style={[styles.progressTrack, { backgroundColor: theme.border }]}>
            <View
              style={[
                styles.progressFill,
                { backgroundColor: theme.primary, width: `${Math.min(100, (completed / count) * 100)}%` },
              ]}
            />
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
});

export function AudioListenScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();

  const series = useAudioLessonsStore(selectAudioSeries);
  const isLoading = useAudioLessonsStore(selectIsLoadingAudioSeries);
  const errorInfo = useAudioLessonsStore(selectAudioSeriesError);
  const fetchSeriesList = useAudioLessonsStore(selectFetchAudioSeriesList);
  const clearError = useAudioLessonsStore(selectClearAudioSeriesError);
  const hydrateProgress = useAudioLessonsStore(selectHydrateAudioProgress);
  const fetchProgress = useAudioLessonsStore(selectFetchAudioProgress);
  const activeCourseId = useUserPreferencesStore(selectActiveCourseId);

  // Grammar group: lesson_linked series bucketed by level (sorted).
  const grammarGroups = useMemo(() => {
    const byLevel = new Map<string, AudioSeriesSummary[]>();
    for (const s of series) {
      if (s.kind !== 'lesson_linked') continue;
      const level = s.level ?? '';
      const bucket = byLevel.get(level);
      if (bucket) bucket.push(s);
      else byLevel.set(level, [s]);
    }
    return [...byLevel.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([level, items]) => ({ level, items }));
  }, [series]);

  const standalone = useMemo(() => series.filter((s) => s.kind === 'standalone'), [series]);

  // Hydrate the local progress cache instantly (offline-safe), then refresh
  // series + the authoritative server progress per course on focus.
  useFocusEffect(
    useCallback(() => {
      void hydrateProgress();
    }, [hydrateProgress]),
  );
  useFocusEffect(
    useCallback(() => {
      if (activeCourseId) {
        void fetchSeriesList(activeCourseId);
        void fetchProgress(activeCourseId);
      }
    }, [activeCourseId, fetchSeriesList, fetchProgress]),
  );

  const handleOpenSeries = useCallback(
    (slug: string) => navigation.navigate('AudioSeries', { slug }),
    [navigation],
  );
  const handleRetry = useCallback(() => {
    clearError();
    if (activeCourseId) void fetchSeriesList(activeCourseId);
  }, [clearError, activeCourseId, fetchSeriesList]);
  const handleRefresh = useCallback(() => {
    if (activeCourseId) void fetchSeriesList(activeCourseId);
  }, [activeCourseId, fetchSeriesList]);

  const renderCard = useCallback(
    ({ item }: { item: AudioSeriesSummary }) => <SeriesCard series={item} onPress={handleOpenSeries} />,
    [handleOpenSeries],
  );
  const cardKey = useCallback((s: AudioSeriesSummary) => s.slug, []);

  const isEmpty = !isLoading && !errorInfo && series.length === 0;

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('audioLessons.title')} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <ScrollView
          testID="audio-lessons-screen"
          contentContainerStyle={styles.scrollContent}
          refreshControl={
            <RefreshControl
              refreshing={isLoading && series.length > 0}
              onRefresh={handleRefresh}
              tintColor={theme.primary}
            />
          }
        >
          {errorInfo && (
            <View
              testID="audio-lessons-error"
              style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
            >
              <Text allowFontScaling={false} style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}>
                {errorMessage(errorInfo)}
              </Text>
              <TouchableOpacity
                testID="audio-lessons-error-retry"
                accessibilityRole="button"
                accessibilityLabel={t('audioLessons.loadErrorRetry')}
                onPress={handleRetry}
                style={[styles.retryButton, { borderColor: theme.border }]}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                >
                  {t('audioLessons.loadErrorRetry')}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {isLoading && series.length === 0 && !errorInfo && (
            <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 24 }} />
          )}

          {isEmpty && (
            <View testID="audio-lessons-empty" style={styles.emptyHint}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: 14 * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
              >
                {t('audioLessons.emptyHint')}
              </Text>
            </View>
          )}

          {grammarGroups.length > 0 && (
            <View style={styles.section}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: 18 * fontScale, fontWeight: '700', color: theme.text, marginBottom: 8, ...ffs }}
              >
                {t('audioLessons.grammarGroupTitle')}
              </Text>
              {grammarGroups.map((group) => (
                <View key={group.level || 'none'} style={{ marginBottom: 8 }}>
                  {group.level.length > 0 && (
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: 13 * fontScale, fontWeight: '600', color: theme.textSecondary, marginBottom: 6, ...ffs }}
                    >
                      {group.level}
                    </Text>
                  )}
                  <FlatList
                    data={group.items}
                    renderItem={renderCard}
                    keyExtractor={cardKey}
                    numColumns={2}
                    columnWrapperStyle={{ gap: 12 }}
                    contentContainerStyle={{ gap: 12 }}
                    scrollEnabled={false}
                  />
                </View>
              ))}
            </View>
          )}

          {standalone.length > 0 && (
            <View style={styles.section}>
              <FlatList
                data={standalone}
                renderItem={renderCard}
                keyExtractor={cardKey}
                numColumns={2}
                columnWrapperStyle={{ gap: 12 }}
                contentContainerStyle={{ gap: 12 }}
                scrollEnabled={false}
              />
            </View>
          )}
        </ScrollView>
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: { padding: 16, gap: 12 },
  section: { gap: 8 },
  cardWrap: { flex: 1 },
  card: { padding: 14, borderRadius: 12, borderWidth: 1, minHeight: 110, flex: 1 },
  levelPill: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    borderWidth: 1,
  },
  progressTrack: { height: 4, borderRadius: 2, overflow: 'hidden', marginTop: 10 },
  progressFill: { height: '100%', borderRadius: 2 },
  emptyHint: { alignItems: 'center', paddingVertical: 24 },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  retryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
});
